import { useDevice } from "@revolt/common";
import { preloadProfile } from "@revolt/ui/components/features/profiles/profileCache";
import {
  type Accessor,
  type JSX,
  createEffect,
  createSignal,
  on,
  onCleanup,
} from "solid-js";

/** Konkord: how long the mouse stays on someone before their card is prepared */
const PRELOAD_HOVER_MS = 150;

type Props = JSX.Directives["floating"] & object;

/**
 * Konkord: a click this soon after a press that found the menu open only
 * closes it (see onPressClick)
 */
const PRESS_CLOSE_MS = 1000;

export type FloatingElement = {
  config: () => Props;
  element: HTMLElement;
  hide: () => void;
  show: Accessor<Props | undefined>;
};

const [floatingElements, setFloatingElements] = createSignal<FloatingElement[]>(
  [],
);

export { floatingElements };

/**
 * Register a new floating element
 * @param element element
 */
export function registerFloatingElement(element: FloatingElement) {
  setFloatingElements((elements) => [...elements, element]);
}

/**
 * Un register floating element
 * @param element DOM Element
 */
export function unregisterFloatingElement(element: HTMLElement) {
  setFloatingElements((elements) =>
    elements.filter((entry) => entry.element !== element),
  );
}

/**
 * Add floating elements
 * @param element Element
 * @param accessor Parameters
 */
export function floating(element: HTMLElement, accessor: Accessor<Props>) {
  if (!accessor()) return;

  const { isIOSTouch } = useDevice();

  const [show, setShow] = createSignal<Props | undefined>();
  // DEBUG: createEffect(() => console.info("show:", show()));

  registerFloatingElement({
    config: accessor,
    element,
    show,
    /**
     * Hide the element
     */
    hide() {
      setShow(undefined);
    },
  });

  /**
   * Trigger a floating element
   */
  function trigger(target: keyof Props, desiredState?: boolean) {
    const current = show();
    const config = accessor();

    if (target === "userCard" && config.userCard) {
      if (current?.userCard) {
        setShow(undefined);
      } else if (!current) {
        setShow({ userCard: config.userCard });
      } else {
        setShow(undefined);
        setShow({ userCard: config.userCard });
      }
    }

    if (target === "tooltip" && config.tooltip) {
      if (current?.tooltip) {
        if (desiredState !== true) {
          setShow(undefined);
        }
      } else if (!current) {
        if (desiredState !== false) {
          setShow({ tooltip: config.tooltip });
        }
      }
    }

    if (target === "contextMenu" && config.contextMenu) {
      if (current?.contextMenu) {
        setShow(undefined);
      } else if (!current) {
        setShow({ contextMenu: config.contextMenu });
      } else {
        setShow(undefined);
        setShow({ contextMenu: config.contextMenu });
      }
    }
  }

  /**
   * Handle click events
   */
  function onClick() {
    // TODO: handle shift+click for mention
    trigger("userCard");
  }

  // Konkord: the mouse stopped on someone: their card opens at once on the click
  let preloadTimer: ReturnType<typeof setTimeout> | undefined;
  function onCardHover() {
    clearTimeout(preloadTimer);
    preloadTimer = setTimeout(() => {
      const user = accessor().userCard?.user;
      if (user) preloadProfile(user);
    }, PRELOAD_HOVER_MS);
  }
  function onCardLeave() {
    clearTimeout(preloadTimer);
  }

  /**
   * Handle context menu click
   */
  function onContextMenu(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    // Konkord: a right click on the element whose menu was open closes it
    // (the press already hid it, don't open it again)
    const rightPressed = rightPressedOpenAt;
    rightPressedOpenAt = undefined;
    if (
      event.type === "contextmenu" &&
      rightPressed !== undefined &&
      performance.now() - rightPressed < PRESS_CLOSE_MS
    )
      return;

    trigger("contextMenu");
  }

  /**
   * Konkord: a left press on a button (or an arrow / user card) while its
   * menu is open only closes it (the press elsewhere already does). Without
   * this the press closed it and the click opened it again (arrow buttons),
   * or the click also ran the button's own action (mute while closing the
   * device menu). Links and buttons inside the element keep their clicks.
   */
  let pressedOpenAt: number | undefined;
  let rightPressedOpenAt: number | undefined;

  function onPressStart(event: PointerEvent) {
    const current = show();
    rightPressedOpenAt =
      event.button === 2 && current?.contextMenu
        ? performance.now()
        : undefined;
    const config = accessor();
    pressedOpenAt =
      event.button === 0 &&
      (current?.userCard ||
        (current?.contextMenu &&
          (config.contextMenuHandler === "click" ||
            element.tagName === "BUTTON")))
        ? performance.now()
        : undefined;
  }

  function onPressClick(event: MouseEvent) {
    const pressed = pressedOpenAt;
    pressedOpenAt = undefined;
    if (
      pressed === undefined ||
      // a click from the keyboard, or long after the press
      event.detail === 0 ||
      performance.now() - pressed > PRESS_CLOSE_MS
    )
      return;
    const inner = (event.target as Element | null)?.closest?.(
      "a, button, input, select, textarea",
    );
    if (inner && inner !== element && element.contains(inner)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    setShow(undefined);
  }

  const onPressCancel = () => (pressedOpenAt = undefined);

  element.addEventListener("pointerdown", onPressStart);
  element.addEventListener("pointercancel", onPressCancel);
  element.addEventListener("click", onPressClick, true);
  onCleanup(() => {
    element.removeEventListener("pointerdown", onPressStart);
    element.removeEventListener("pointercancel", onPressCancel);
    element.removeEventListener("click", onPressClick, true);
  });

  let isTouching = false,
    tTmr: NodeJS.Timeout | undefined;

  /**
   * Handle mouse entering
   */
  function onMouseEnter() {
    if (!isTouching) trigger("tooltip", true);
  }

  /**
   * Handle mouse leaving
   */
  function onMouseLeave() {
    trigger("tooltip", false);
  }

  function onTouch() {
    isTouching = true;
    clearTimeout(tTmr);
    tTmr = setTimeout(() => {
      isTouching = false;
      tTmr = undefined;
    }, 100);
  }

  createEffect(
    on(
      () => accessor().userCard,
      (userCard) => {
        if (userCard) {
          element.style.cursor = "pointer";
          element.style.userSelect = "none";
          element.addEventListener("click", onClick);
          element.addEventListener("mouseenter", onCardHover);
          element.addEventListener("mouseleave", onCardLeave);

          onCleanup(() => {
            clearTimeout(preloadTimer);
            element.removeEventListener("click", onClick);
            element.removeEventListener("mouseenter", onCardHover);
            element.removeEventListener("mouseleave", onCardLeave);
          });
        }
      },
    ),
  );

  createEffect(
    on(
      () => accessor().tooltip,
      (tooltip) => {
        if (tooltip) {
          element.ariaLabel =
            typeof tooltip.content === "string"
              ? tooltip.content
              : tooltip!.aria!;

          element.addEventListener("mouseenter", onMouseEnter);
          element.addEventListener("mouseleave", onMouseLeave);
          element.addEventListener("touchstart", onTouch);
          element.addEventListener("touchend", onTouch);

          onCleanup(() => {
            element.removeEventListener("mouseenter", onMouseEnter);
            element.removeEventListener("mouseleave", onMouseLeave);
            element.removeEventListener("touchstart", onTouch);
            element.removeEventListener("touchend", onTouch);
          });
        }
      },
    ),
  );

  createEffect(
    on(
      () => accessor().contextMenu,
      (contextMenu) => {
        if (contextMenu) {
          // Konkord: remembered, the config may have changed by cleanup time
          // (a click listener left behind would swallow the button's clicks)
          const handler = accessor().contextMenuHandler ?? "contextmenu";
          if (handler === "contextmenu" && isIOSTouch) {
            element.addEventListener("long-press", onContextMenu);
          } else {
            element.addEventListener(handler, onContextMenu);
          }

          onCleanup(() => {
            if (isIOSTouch) {
              element.removeEventListener("long-press", onContextMenu);
            }
            element.removeEventListener(handler, onContextMenu);
          });
        }
      },
    ),
  );

  onCleanup(() => unregisterFloatingElement(element));
}
